import React from 'react';
import { truncateText, typographyGroupLabel } from 'lib/core';
import { removeObjectProperties } from 'lib/utilities';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

const StyledInputLabel = styled.span`
  ${({ theme }) => typographyGroupLabel(theme)}
  ${truncateText}
  box-sizing: border-box;
  pointer-events: none;
  width: 100%;
  ${({ $isDisabled, $isFocused, theme }) =>
    $isFocused &&
    !$isDisabled &&
    css`
      color: ${theme.color.primary['500'].value};
    `}
  ${({ $hasError, $isDisabled, theme }) =>
    $hasError &&
    !$isDisabled &&
    css`
      color: ${theme.color.system.negative[500].value};
    `}
    ${({ $isReadOnly, theme }) =>
    $isReadOnly &&
    css`
      color: ${theme.color.text.light.secondary.value};
    `}
`;

const InputLabel = styled(
  ({
    customTag = undefined,
    dataTestId = undefined,
    hasError = false,
    id = undefined,
    isDisabled = false,
    isFocused = false,
    isReadOnly = false,
    isRequired = false,
    label,
    ...other
  }) => {
    const testId = dataTestId ?? undefined;
    const filteredOtherProps = removeObjectProperties(other, 'labelAlign');
    return (
      <StyledInputLabel
        as={customTag}
        data-testid={testId}
        $hasError={hasError}
        id={id}
        $isDisabled={isDisabled}
        $isFocused={isFocused}
        $isReadOnly={isReadOnly}
        $isRequired={isRequired}
        {...filteredOtherProps}
      >
        {label}
      </StyledInputLabel>
    );
  }
)``;

InputLabel.propTypes = {
  /** Replaces the default tag with a new tag */
  customTag: PropTypes.elementType,
  /** Used to identify the span element in testing */
  dataTestId: PropTypes.string,
  /** If true, adjusts label styling */
  hasError: PropTypes.bool,
  /** The id of the html span */
  id: PropTypes.string,
  /** If true, adjusts label styling */
  isDisabled: PropTypes.bool,
  /** If true, label has focus */
  isFocused: PropTypes.bool,
  /** If true, adjusts label styling */
  isReadOnly: PropTypes.bool,
  /** If true, adds required styling */
  isRequired: PropTypes.bool,
  /** Label of the input field */
  label: PropTypes.node.isRequired,
};

export { InputLabel };
